/**
 * 可观测性全局维度（P0-12/13）
 *
 * 解决两处历史硬伤：
 *  - model_id 是 26 位随机串，看板无法阅读 → 统一改用可读的 model_name + model_tier
 *  - pid 取原始 pathname，动态路由（/canvas/:id）会把页面维度基数打爆 → 归一化为路由模板
 *
 * 维度值一律低基数、可读、稳定（见 docs/20260819_frontend_observability_plan.md 4.8 第 6 条），
 * 一旦上线不可再改取值格式，否则历史数据与新数据无法对比。
 */

import apmClient from '@apmplus/web';

export type Feature = 'canvas' | 'text' | 'image' | 'video' | 'tools' | 'home' | 'assets' | 'other';
export type ModelTier = 'primary' | 'secondary' | 'longtail';
export type ScaleBucket = '0-50' | '50-200' | '200-500' | '500+';

// ─── pid 路由归一化（P0-13）───────────────────────────────────────────────
// 动态路径统一收敛为路由模板，防止每个 canvasId 生成一个独立 pid。
const PID_RULES: [RegExp, string][] = [
  [/^\/canvas\/[^/]+/, '/canvas/:id'],
  [/^\/director\/[^/]+/, '/director/:id'],
  [/^\/assets\/[^/]+\/[^/]+/, '/assets/:id/:modelIds'],
  [/^\/assets\/[^/]+/, '/assets/:id'],
  [/^\/creation\/video\/[^/]+/, '/creation/video/:id'],
];

export const normalizePid = (pathname: string): string =>
  PID_RULES.find(([re]) => re.test(pathname))?.[1] ?? pathname;

// ─── feature 功能区归一化 ─────────────────────────────────────────────────
const FEATURE_RULES: [RegExp, Feature][] = [
  [/^\/(canvas|director)(\/|$)/, 'canvas'],
  [/^\/creation\/text/, 'text'],
  [/^\/creation\/image/, 'image'],
  [/^\/creation\/(edit-)?video/, 'video'],
  [/^\/(editor|creation\/tools)/, 'tools'],
  [/^\/assets/, 'assets'],
  [/^\/(home)?$/, 'home'],
];

export const resolveFeature = (pathname: string): Feature =>
  FEATURE_RULES.find(([re]) => re.test(pathname))?.[1] ?? 'other';

/** 路由变化时调用（router.subscribe）：pid 走 config（官方六字段之一），feature 走 context */
export function syncRouteContext(pathname: string): void {
  try {
    apmClient.config({ pid: normalizePid(pathname) });
    apmClient['context.merge']?.({ feature: resolveFeature(pathname) });
    currentFeature = resolveFeature(pathname);
  } catch {
    // 上下文同步失败不应影响页面渲染
  }
}

// ─── model 维度可读化（P0-12）──────────────────────────────────────────────
// model_id 是 ULID（如 01KKATV8C91R7739XY9CKVHQ5P），禁止直接进 context/categories。
/** 去掉版本号里的日期/构建号后再转小写，防止同一模型的不同版本被算作不同模型 */
export const normalizeModelName = (rawName: string | undefined | null): string => {
  if (!rawName) return 'unknown';
  return rawName
    .toLowerCase()
    .replace(/-\d{4}-\d{2}-\d{2}.*$/, '') // 去掉形如 -2026-05-13 的日期后缀
    .replace(/-v?\d+(\.\d+)*$/, '') // 去掉形如 -v2 / -3.0 的版本号后缀
    .trim();
};

// 主力模型名单（低基数兜底用），由业务侧按实际流量分布维护
const PRIMARY_MODELS = new Set<string>();

/** Category 仅展示 Top20，模型数超过 20 时长尾模型会被挤出看板；用 tier 兜底可先筛 longtail 再看 */
export const tierOf = (modelName: string): ModelTier => {
  if (PRIMARY_MODELS.has(modelName)) return 'primary';
  return modelName === 'unknown' ? 'longtail' : 'secondary';
};

let currentFeature: Feature = 'other';
let currentModelName = 'unknown';
let currentTaskType = 'unknown';

/** 选定模型时调用：接口/性能看板即可按模型筛选成功率与耗时分位 */
export function setModelContext(rawModelName: string, taskType: string): void {
  const model_name = normalizeModelName(rawModelName);
  currentModelName = model_name;
  currentTaskType = taskType;
  try {
    apmClient['context.merge']?.({ model_name, model_tier: tierOf(model_name), taskType });
  } catch {
    // 忽略
  }
}

export const bucket = (n: number): ScaleBucket =>
  n <= 50 ? '0-50' : n <= 200 ? '50-200' : n <= 500 ? '200-500' : '500+';

/** 画布规模变化时调用（加载完成/保存时）：官方看板可按 canvasScale 筛选切片 */
export function setCanvasScaleContext(nodeCount: number): void {
  try {
    apmClient['context.merge']?.({ canvasScale: bucket(nodeCount) });
  } catch {
    // 忽略
  }
}

/** 供快照模块读取，避免每处都重新推导当前功能区/模型 */
export const getCurrentFeature = (): Feature => currentFeature;
export const getCurrentModelName = (): string => currentModelName;
export const getCurrentTaskType = (): string => currentTaskType;
